import React, { useState } from 'react';
import {
  Grid, Paper, TextField, Button, Typography, MenuItem, RadioGroup, FormControlLabel,
  Radio, Table, TableBody, TableCell, TableHead, TableRow, makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { marketingService, authService } from '../services';
import { PRODUCTS } from '../constants/product.constant';

const useStyles = makeStyles(() => ({
  header: { fontWeight: 600, marginBottom: 4 },
  sub: { color: '#6b7280', fontSize: 13, marginBottom: 16 },
  form: { padding: 24, maxWidth: 680 },
  title: { fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 16 },
  field: { marginBottom: 16 },
  sectionLabel: { fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 4 },
  hint: { color: '#6b7280', fontSize: 12, marginTop: -8, marginBottom: 16 },
  actions: { marginTop: 8 },
  alert: { marginTop: 16 },
  results: { marginTop: 20, padding: 16 },
  ok: { color: '#047857', fontWeight: 600 },
  fail: { color: '#b91c1c', fontWeight: 600 },
}));

const AUDIENCE_OPTIONS = [
  { value: '', label: 'All existing users' },
  ...PRODUCTS.map((p) => ({ value: p.key, label: p.label })),
];

type TestResult = { to: string; success: boolean; error?: string };

const MarketingScreen: React.FC = () => {
  const classes = useStyles();

  // Full-access admins can broadcast to everyone ('All') or any product.
  // Restricted admins may only target their own products (no 'All').
  const audienceOptions = authService.isFullAccess
    ? AUDIENCE_OPTIONS
    : AUDIENCE_OPTIONS.filter((o) => o.value !== '' && authService.hasProduct(o.value));

  const [mode, setMode] = useState<'test' | 'broadcast'>('test');
  const [message, setMessage] = useState('');
  const [numbersRaw, setNumbersRaw] = useState('');
  const [audience, setAudience] = useState(audienceOptions[0]?.value || '');

  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [results, setResults] = useState<TestResult[] | null>(null);

  const parseNumbers = (raw: string) =>
    raw.split(/[\s,;\n]+/).map((n) => n.trim()).filter(Boolean);

  const handleSend = async () => {
    setError('');
    setSuccess('');
    setResults(null);

    if (!message.trim()) {
      setError('Message is required.');
      return;
    }

    if (mode === 'test') {
      const numbers = parseNumbers(numbersRaw);
      if (!numbers.length) {
        setError('Enter at least one test number.');
        return;
      }
      // eslint-disable-next-line no-restricted-globals
      if (!window.confirm(`Send this WhatsApp message to ${numbers.length} test number(s)?`)) return;

      setSending(true);
      const res = await marketingService.test(message.trim(), numbers);
      setSending(false);
      if (!res.ok) { setError(res.error || 'Failed to send.'); return; }
      const d = res.data || {};
      setResults(d.results || []);
      setSuccess(`Processed ${d.results?.length ?? 0} number(s): ${d.sent ?? 0} sent, ${d.failed ?? 0} failed.`);
      return;
    }

    // broadcast
    const audienceLabel = AUDIENCE_OPTIONS.find((o) => o.value === audience)?.label || 'all users';
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Send this WhatsApp message to ${audienceLabel}? This reaches every matching existing user.`)) return;

    setSending(true);
    const res = await marketingService.broadcast(message.trim(), audience);
    setSending(false);
    if (!res.ok) { setError(res.error || 'Failed to start broadcast.'); return; }
    const d = res.data || {};
    setSuccess(
      d.started
        ? `Broadcast started — sending to ${d.recipients ?? 0} user(s) in the background.`
        : `No recipients matched ${audienceLabel}.`,
    );
  };

  return (
    <div>
      <Typography variant="h5" className={classes.header}>Marketing Reach-out</Typography>
      <Typography className={classes.sub}>WhatsApp messaging to existing users via the Cloud API.</Typography>

      <Grid container>
        <Grid item xs={12}>
          <Paper className={classes.form} elevation={1}>
            <Typography className={classes.title}>Compose a WhatsApp message</Typography>

            <Typography className={classes.sectionLabel}>Mode</Typography>
            <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value as 'test' | 'broadcast')}>
              <FormControlLabel value="test" control={<Radio color="primary" />} label="Test (specific numbers)" />
              <FormControlLabel value="broadcast" control={<Radio color="primary" />} label="All existing users" />
            </RadioGroup>

            {mode === 'test' ? (
              <>
                <TextField
                  className={classes.field}
                  fullWidth
                  label="Test numbers"
                  placeholder="9876543210, 9123456789 (comma / space / newline separated)"
                  variant="outlined"
                  size="small"
                  multiline
                  minRows={2}
                  value={numbersRaw}
                  onChange={(e) => setNumbersRaw(e.target.value)}
                />
                <div className={classes.hint}>10-digit numbers get a 91 prefix automatically.</div>
              </>
            ) : (
              <TextField
                className={classes.field}
                select
                fullWidth
                label="Audience"
                variant="outlined"
                size="small"
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
              >
                {audienceOptions.map((o) => (
                  <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              className={classes.field}
              fullWidth
              label="Message"
              variant="outlined"
              size="small"
              multiline
              minRows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              inputProps={{ maxLength: 1000 }}
            />

            <div className={classes.actions}>
              <Button variant="contained" color="primary" onClick={handleSend} disabled={sending}>
                {sending ? 'Sending…' : mode === 'test' ? 'Send test' : 'Send broadcast'}
              </Button>
            </div>

            <Alert severity="info" className={classes.alert}>
              Free-form text only reaches users inside WhatsApp's 24-hour window (recently active).
              Cold outreach to everyone else needs a Meta-approved template — those sends will fail.
              Use Test mode to verify before broadcasting.
            </Alert>

            {error && <Alert severity="error" className={classes.alert}>{error}</Alert>}
            {success && <Alert severity="success" className={classes.alert}>{success}</Alert>}
          </Paper>
        </Grid>

        {results && results.length > 0 && (
          <Grid item xs={12}>
            <Paper className={classes.results} elevation={1}>
              <Typography className={classes.title}>Test results</Typography>
              <div style={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow><TableCell>Number</TableCell><TableCell>Status</TableCell><TableCell>Detail</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {results.map((r, i) => (
                    <TableRow key={`${r.to}-${i}`}>
                      <TableCell>{r.to}</TableCell>
                      <TableCell className={r.success ? classes.ok : classes.fail}>{r.success ? 'Sent' : 'Failed'}</TableCell>
                      <TableCell>{r.error || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            </Paper>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default MarketingScreen;
